import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Image,
  Stack,
  useToast,
  FormControl,
  FormLabel,
  SimpleGrid,
  Tooltip,
  IconButton,
  Button,
  Badge,
  Text,
  Input,
  NumberInput,
  NumberInputField,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";

import {
  HiOutlineUpload,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlinePlay,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "../Navbar";
import HeroImage from "../images/stamps.png";
import AllocationTreeEditor from "./AllocationTreeEditor";
import FactorEditor from "./FactorEditor"; // Your existing flat factor editor

/* -----------------------
   Helpers
------------------------*/
const currency = (n) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : n ?? "";

/** Shape a Panner to a clean payload the API expects */
const shapePannerForApi = (p) => {
  const cleanFactorValues = (vals = []) =>
    vals.map((v) => {
      const percent = Number(v.allocationPercent ?? 0);
      const amount = Number(v.allocationAmount ?? 0);
      const cleanPercent = isNaN(percent) ? 0 : percent;
      const cleanAmount = isNaN(amount) ? 0 : amount;
      return {
        id: v.id || crypto.randomUUID(),
        type: v.type ?? "",
        agencyCode: v.agencyCode ?? "",
        agencyCodeSet: v.agencyCodeSet ?? "",
        valueCodeSet: v.valueCodeSet ?? "",
        code: v.code ?? "",
        description: v.description ?? "",
        allocationPercent: cleanPercent,
        allocationAmount: cleanAmount,
      };
    });

  const cleanFactors = (factors = []) =>
    factors.map((f) => ({
      id: f.id || crypto.randomUUID(),
      type: f.type ?? "",
      name: f.name ?? "",
      level: Number.isFinite(f.level) ? f.level : 0,
      agencyCode: f.agencyCode ?? "",
      agencyCodeSet: f.agencyCodeSet ?? "",
      allocationType: f.allocationType ?? "",
      isPartial: !!f.isPartial,
      order: Number.isFinite(f.order) ? f.order : 0,
      values: cleanFactorValues(f.values),
    }));

  // Recursively clean allocation tree nodes
  const cleanAllocationNode = (node) => {
    if (!node) return null;
    const percent = Number(node.allocationPercent ?? 0);
    const amount = Number(node.allocationAmount ?? 0);
    return {
      id: node.id || crypto.randomUUID(),
      name: node.name ?? "",
      level: Number.isFinite(node.level) ? node.level : 0,
      allocationPercent: isNaN(percent) ? 0 : percent,
      allocationAmount: isNaN(amount) ? 0 : amount,
      // API expects lowercase field names for tree nodes (not camelCase)
      factortype: node.factorType ?? "",
      agencycode: node.agencyCode ?? "",
      agencycodeset: node.agencyCodeSet ?? "",
      factorValues: cleanFactorValues(node.factorValues || []),
      // Legacy fields (kept for backward compatibility)
      factorId: node.factorId ?? "",
      factorValueId: node.factorValueId ?? "",
      children: (node.children || []).map(cleanAllocationNode),
    };
  };

  return {
    _id: p._id,
    name: p.name ?? "",
    definitionId: p.name ?? "",
    premium: Number(p.premium ?? 0),
    placingFactors: cleanFactors(p.placingFactors),
    accountingFactors: cleanFactors(p.accountingFactors),
    placingAllocationTree: cleanAllocationNode(p.placingAllocationTree),
    accountingAllocationTree: cleanAllocationNode(p.accountingAllocationTree),
    projections: p.projections ?? [],
    createdDate: p.createdDate ?? new Date().toISOString(),
  };
};

/* -----------------------
   Projections Display
------------------------*/
const ProjectionsTabs = ({ projections = [], onMapfaculate, lookup }) => {
  const [tab, setTab] = useState(
    projections.length ? projections.length - 1 : 0
  );

  useEffect(() => {
    if (projections.length) setTab(projections.length - 1);
  }, [projections.length]);

  const labelFor = (p, i) => {
    if (p?.type && String(p.type).trim()) return String(p.type);
    return `Projection ${i + 1}`;
  };

  const FactorValueListTable = ({ title, items = [] }) => (
    <Box borderWidth="1px" borderRadius="md" p={2} backgroundColor="whiteAlpha.700">
      <Heading size="xs" mb={2}>{title}</Heading>
      <Box overflowX="auto">
        <table style={{ width: "100%", fontSize: "0.875rem" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px" }}>Key</th>
              <th style={{ textAlign: "left", padding: "4px" }}>Factor</th>
              <th style={{ textAlign: "left", padding: "4px" }}>Value</th>
              <th style={{ textAlign: "right", padding: "4px" }}>Allocation%</th>
              <th style={{ textAlign: "right", padding: "4px" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ color: "gray", fontStyle: "italic", padding: "8px" }}>
                  No entries
                </td>
              </tr>
            ) : (
              items.map((fv) => {
                const factorLabel =
                  lookup?.factorLabel(fv.factorId) ||
                  lookup?.factorLabel(fv.factor_id) ||
                  "";
                const valueLabel =
                  fv.valueCode ||
                  lookup?.valueLabel(fv.valueId) ||
                  lookup?.valueLabel(fv.value_id) ||
                  "";
                const allocPct =
                  typeof fv.allocationValue === "number"
                    ? fv.allocationValue.toFixed(6)
                    : fv.allocationValue ?? "";
                const allocAmt =
                  typeof fv.allocationAmount === "number"
                    ? currency(fv.allocationAmount)
                    : fv.allocationAmount ?? "";
                return (
                  <tr key={fv.id || `${fv.factorId}-${fv.valueId}-${fv.key}`}>
                    <td style={{ padding: "4px" }}>{fv.key}</td>
                    <td style={{ padding: "4px" }}>{factorLabel}</td>
                    <td style={{ padding: "4px" }}>{valueLabel}</td>
                    <td style={{ textAlign: "right", padding: "4px" }}>{allocPct}</td>
                    <td style={{ textAlign: "right", padding: "4px" }}>{allocAmt}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} backgroundColor="whiteAlpha.800">
      <HStack justify="space-between" mb={2}>
        <Heading size="sm">Projections</Heading>
        <Badge>{projections.length}</Badge>
      </HStack>

      {projections.length === 0 ? (
        <Box mt={2} color="gray.600" fontStyle="italic">
          No projections yet — click "Generate from Tree".
        </Box>
      ) : (
        <Tabs
          index={tab}
          onChange={setTab}
          isLazy
          variant="enclosed"
          orientation="vertical"
          size="sm">
          <TabList overflowX="auto" whiteSpace="nowrap">
            {projections.map((p, i) => (
              <Tab key={p.id || p._id || i}>{labelFor(p, i)}</Tab>
            ))}
          </TabList>

          <TabPanels>
            {projections.map((p, i) => {
              const segments = Array.isArray(p.segments) ? p.segments : [];

              return (
                <TabPanel key={p.id || p._id || i} px={0}>
                  <HStack mt={2} mb={2} spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<HiOutlinePlay />}
                      onClick={() => onMapfaculate(p.id || p._id)}
                      isDisabled={!(p?.id || p?._id)}>
                      Map & Allocate
                    </Button>
                  </HStack>

                  {segments.length === 0 ? (
                    <Text color="gray.600" fontStyle="italic">
                      This projection has no segments.
                    </Text>
                  ) : (
                    <Stack spacing={3}>
                      {segments.map((s) => {
                        const allocPct =
                          typeof s.allocationValue === "number"
                            ? s.allocationValue.toFixed(6)
                            : s.allocationValue ?? "";
                        const premium =
                          typeof s.premium === "number"
                            ? currency(s.premium)
                            : s.premium ?? "";

                        return (
                          <Box
                            key={s.id}
                            borderWidth="1px"
                            borderRadius="md"
                            p={2}
                            backgroundColor="gray.50">
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                              <Box borderWidth="1px" borderRadius="md" p={2} bg="white">
                                <Heading size="xs" mb={2}>Premium Segment</Heading>
                                <Text fontSize="xs" color="gray.600">Allocation %</Text>
                                <Text fontSize="sm">{allocPct}%</Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>Premium</Text>
                                <Text fontSize="sm">{premium}</Text>
                                {p.type === "placing" && (
                                  <>
                                    <Text fontSize="xs" color="gray.600" mt={1}>Placing Key</Text>
                                    <Text fontSize="xs" fontFamily="mono">{s.placingKey}</Text>
                                  </>
                                )}
                                <Text fontSize="xs" color="gray.600" mt={1}>Accounting Key</Text>
                                <Text fontSize="xs" fontFamily="mono">{s.accountingKey}</Text>
                              </Box>

                              {p.type === "placing" && (
                                <FactorValueListTable
                                  title="Placing Factors"
                                  items={Array.isArray(s.placingFactorValueIds) ? s.placingFactorValueIds : []}
                                />
                              )}

                              <FactorValueListTable
                                title="Accounting Factors"
                                items={Array.isArray(s.accountingFactorValueIds) ? s.accountingFactorValueIds : []}
                              />
                            </SimpleGrid>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};

// Build lookup maps for factor/value labels
const buildLookups = (panner) => {
  const factorById = new Map();
  const valueById = new Map();

  const add = (arr = []) => {
    arr.forEach((f) => {
      if (f?.id) factorById.set(f.id, f);
      (f?.values || []).forEach((v) => {
        if (v?.id) valueById.set(v.id, v);
      });
    });
  };

  add(panner?.placingFactors);
  add(panner?.accountingFactors);

  return {
    factorLabel: (id) => {
      const f = factorById.get(id);
      return f?.type || f?.name || id || "";
    },
    valueLabel: (id) => {
      const v = valueById.get(id);
      return v?.code || v?.description || id || "";
    },
  };
};

/** Main form with tree-based allocation */
const PannerForm = ({
  panner,
  setPanner,
  onSave,
  onGenerate,
  onMapfaculate,
}) => {
  const set = (patch) => setPanner({ ...panner, ...patch });
  const projections = panner?.projections || [];
  const toast = useToast();

  const handlePlacingTreeChange = (updatedTree) => {
    set({ placingAllocationTree: updatedTree });
  };

  const handleAccountingTreeChange = (updatedTree) => {
    set({ accountingAllocationTree: updatedTree });
  };

  // Handle copying factors from placing tree to accounting tree
  const handleCopyFactors = (factorConfig, level) => {
    if (!panner?.accountingAllocationTree) return;

    // Helper to find all leaf nodes (nodes without children) in the tree
    const findLeafNodes = (node) => {
      if (!node.children || node.children.length === 0) {
        return [node];
      }
      return node.children.flatMap((child) => findLeafNodes(child));
    };

    // Helper to update all leaf nodes with factor config
    const updateLeafNodes = (node) => {
      if (!node.children || node.children.length === 0) {
        // This is a leaf node - copy factor configuration
        // Deep copy factor values with new IDs, or create a default one if empty
        let copiedFactorValues;
        if (factorConfig.factorValues && factorConfig.factorValues.length > 0) {
          copiedFactorValues = factorConfig.factorValues.map((fv) => ({
            ...fv,
            id: crypto.randomUUID(), // New ID for the copy
          }));
        } else {
          // Create a default factor value if none exist
          copiedFactorValues = [
            {
              id: crypto.randomUUID(),
              code: "",
              description: "",
              agencyCode: factorConfig.agencyCode || "",
              agencyCodeSet: factorConfig.agencyCodeSet || "",
              allocationPercent: 100,
              allocationAmount: 0,
              isCustom: false,
            },
          ];
        }

        // Copy factor configuration
        return {
          ...node,
          factorType: factorConfig.factorType,
          agencyCode: factorConfig.agencyCode,
          agencyCodeSet: factorConfig.agencyCodeSet,
          factorValues: copiedFactorValues,
        };
      }
      // Not a leaf node - recurse into children
      return {
        ...node,
        children: node.children.map((child) => updateLeafNodes(child)),
      };
    };

    // Find how many leaf nodes exist in accounting tree
    const leafNodes = findLeafNodes(panner.accountingAllocationTree);

    if (leafNodes.length === 0) {
      toast({
        title: "No leaf nodes found",
        description: "The Accounting tree has no leaf nodes to copy factors to.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Update accounting tree with copied factors
    const updatedTree = updateLeafNodes(panner.accountingAllocationTree);
    set({ accountingAllocationTree: updatedTree });

    toast({
      title: "Factors copied",
      description: `Copied factor configuration to ${leafNodes.length} leaf node(s) in the Accounting tree.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Recalculate tree amounts when premium changes
  useEffect(() => {
    if (panner?.placingAllocationTree && panner?.premium !== undefined) {
      const recalc = (node, parentAmount) => {
        const amount = parentAmount * ((node.allocationPercent || 0) / 100);
        return {
          ...node,
          allocationAmount: amount,
          children: (node.children || []).map(child => recalc(child, amount)),
        };
      };

      const rootWithAmount = {
        ...panner.placingAllocationTree,
        allocationAmount: panner.premium,
      };

      const updated = {
        ...rootWithAmount,
        children: (rootWithAmount.children || []).map(child => 
          recalc(child, panner.premium)
        ),
      };

      if (JSON.stringify(updated) !== JSON.stringify(panner.placingAllocationTree)) {
        set({ placingAllocationTree: updated });
      }
    }
  }, [panner?.premium]);

  return (
    <Stack spacing={4}>
      {/* Header / Basic Info */}
      <Box borderWidth="1px" borderRadius="md" p={3} backgroundColor="whiteAlpha.800">
        <FormControl mb={3}>
          <FormLabel fontSize="sm" fontWeight="bold">Panner Name</FormLabel>
          <Input
            size="md"
            value={panner?.name || ""}
            onChange={(e) => set({ name: e.target.value })}
          />
        </FormControl>

        <FormControl mb={3}>
          <FormLabel fontSize="sm">Total Premium</FormLabel>
          <NumberInput
            size="md"
            min={0}
            precision={2}
            value={panner?.premium ?? 0}
            onChange={(_, n) => set({ premium: n })}>
            <NumberInputField />
          </NumberInput>
        </FormControl>

        <HStack spacing={2}>
          <Button
            leftIcon={<HiOutlineSave />}
            colorScheme="linkedin"
            onClick={onSave}>
            Save Panner
          </Button>
          <Button
            variant="outline"
            leftIcon={<HiOutlinePlay />}
            onClick={onGenerate}
            isDisabled={!panner?.placingAllocationTree}>
            Generate from Tree
          </Button>
        </HStack>
      </Box>

      {/* Side-by-side: Placing Tree and Accounting Tree */}
      <Box
        display="grid"
        gridTemplateColumns={["1fr", "1fr", "1fr 1fr"]}
        gap={3}
        alignItems="start">

        {/* Left: Placing Allocation Tree */}
        <AllocationTreeEditor
          tree={panner?.placingAllocationTree}
          onChange={handlePlacingTreeChange}
          factors={panner?.placingFactors || []}
          premium={panner?.premium || 0}
          title="Placing Allocation Tree"
          enableFactorCopy={true}
          onCopyFactors={handleCopyFactors}
        />

        {/* Right: Accounting Allocation Tree */}
        <AllocationTreeEditor
          tree={panner?.accountingAllocationTree}
          onChange={handleAccountingTreeChange}
          factors={panner?.accountingFactors || []}
          premium={panner?.premium || 0}
          title="Accounting Allocation Tree"
        />
      </Box>

      {/* Projections Display */}
      <ProjectionsTabs
        projections={projections}
        onMapfaculate={onMapfaculate}
        lookup={buildLookups(panner)}
      />
    </Stack>
  );
};

/* -----------------------
   Main Component
------------------------*/
const PannerTreeBased = () => {
  const [panners, setPanners] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [panner, setPanner] = useState(null);
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const api = `https://panner.poc.mrc.ai/api`;

  const refreshHeaders = async () => {
    try {
      const res = await fetch(`${api}/panners`);
      const data = await res.json();
      setPanners(Array.isArray(data) ? data : []);
    } catch {
      toast({ title: "Error loading panners", status: "error" });
    }
  };

  // Normalize allocation tree from API format to UI format
  const normalizeAllocationTree = (node) => {
    if (!node) return null;

    // Ensure factor values have the required fields
    const normalizedFactorValues = (node.factorValues || []).map(fv => ({
      ...fv,
      id: fv.id || fv._id || crypto.randomUUID(), // Normalize MongoDB _id to id, generate if missing
      code: fv.code ?? "",
      description: fv.description ?? "",
      agencyCode: fv.agencyCode ?? "",
      agencyCodeSet: fv.agencyCodeSet ?? "",
      allocationPercent: fv.allocationPercent ?? 0,
      allocationAmount: fv.allocationAmount ?? 0,
      isCustom: fv.isCustom ?? false,
    }));

    // API returns lowercase field names, but UI uses camelCase
    // Handle both cases for compatibility
    const factorType = node.factorType || node.factortype || "";
    const agencyCode = node.agencyCode || node.agencycode || "";
    const agencyCodeSet = node.agencyCodeSet || node.agencycodeset || "";

    return {
      ...node,
      id: node.id || node._id || crypto.randomUUID(), // Normalize MongoDB _id to id, generate if missing
      name: node.name ?? "",
      level: Number.isFinite(node.level) ? node.level : 0,
      allocationPercent: node.allocationPercent ?? 0,
      allocationAmount: node.allocationAmount ?? 0,
      factorType,
      agencyCode,
      agencyCodeSet,
      factorValues: normalizedFactorValues,
      children: (node.children || []).map(normalizeAllocationTree),
    };
  };

  const loadPanner = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${api}/panners/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Initialize trees if not present
      if (!data.placingAllocationTree) {
        data.placingAllocationTree = {
          id: crypto.randomUUID(),
          name: "Total Premium",
          level: 0,
          allocationPercent: 100,
          allocationAmount: data.premium || 0,
          children: [],
          factorValues: [],
        };
      } else {
        // Normalize the tree structure
        data.placingAllocationTree = normalizeAllocationTree(data.placingAllocationTree);
      }

      if (!data.accountingAllocationTree) {
        data.accountingAllocationTree = {
          id: crypto.randomUUID(),
          name: "Total Premium",
          level: 0,
          allocationPercent: 100,
          allocationAmount: data.premium || 0,
          children: [],
          factorValues: [],
        };
      } else {
        // Normalize the tree structure
        data.accountingAllocationTree = normalizeAllocationTree(data.accountingAllocationTree);
      }

      setPanner(data);
      setSelectedId(id);
    } catch (e) {
      toast({
        title: "Error loading panner",
        description: String(e.message || e),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const createPanner = async () => {
    try {
      const body = {
        name: `New Panner ${new Date().toLocaleString()}`,
        premium: 0,
        placingFactors: [],
        accountingFactors: [],
        placingAllocationTree: {
          id: crypto.randomUUID(),
          name: "Total Premium",
          level: 0,
          allocationPercent: 100,
          allocationAmount: 0,
          children: [],
        },
        accountingAllocationTree: {
          id: crypto.randomUUID(),
          name: "Total Premium",
          level: 0,
          allocationPercent: 100,
          allocationAmount: 0,
          children: [],
        },
        projections: [],
        createdDate: new Date().toISOString(),
      };
      const res = await fetch(`${api}/panners`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast({ title: "Panner created", status: "success" });
      await refreshHeaders();
      await loadPanner(data._id);
    } catch (e) {
      toast({
        title: "Error creating panner",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  const savePanner = async () => {
    if (!panner?._id) return;
    try {
      const payload = shapePannerForApi(panner);
      console.log("Saving payload:", JSON.stringify(payload, null, 2));
      const res = await fetch(`${api}/panners/${panner._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Normalize the tree data after save (same as load)
      if (data.placingAllocationTree) {
        data.placingAllocationTree = normalizeAllocationTree(data.placingAllocationTree);
      }
      if (data.accountingAllocationTree) {
        data.accountingAllocationTree = normalizeAllocationTree(data.accountingAllocationTree);
      }

      setPanner(data);
      toast({ title: "Saved successfully", status: "success" });

      setPanners((prev) =>
        prev.map((h) =>
          h._id === payload._id ? { ...h, name: payload.name } : h
        )
      );

      await refreshHeaders();
    } catch (e) {
      toast({
        title: "Save failed",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  const generate = async () => {
    if (!panner?._id) return;
    try {
      const payload = shapePannerForApi(panner);
      const res = await fetch(`${api}/panners/${panner._id}/project`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Normalize the tree data after generate (same as load/save)
      if (data.placingAllocationTree) {
        data.placingAllocationTree = normalizeAllocationTree(data.placingAllocationTree);
      }
      if (data.accountingAllocationTree) {
        data.accountingAllocationTree = normalizeAllocationTree(data.accountingAllocationTree);
      }

      setPanner(data);
      toast({ title: "Projections generated from tree", status: "success" });
    } catch (e) {
      toast({
        title: "Generate failed",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  const mapfaculate = async (projectionId) => {
    if (!panner?._id || !projectionId) return;
    try {
      const payload = shapePannerForApi(panner);
      const res = await fetch(
        `${api}/panners/${panner._id}/mapfaculate/${encodeURIComponent(projectionId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Normalize the tree data after mapfaculate (same as load/save/generate)
      if (data.placingAllocationTree) {
        data.placingAllocationTree = normalizeAllocationTree(data.placingAllocationTree);
      }
      if (data.accountingAllocationTree) {
        data.accountingAllocationTree = normalizeAllocationTree(data.accountingAllocationTree);
      }

      setPanner(data);
      toast({ title: "Mapped & allocated", status: "success" });
    } catch (e) {
      toast({
        title: "Map/allocate failed",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  useEffect(() => {
    refreshHeaders();
  }, []);

  const filtered = useMemo(() => {
    const t = (filter || "").toLowerCase();
    return (panners || []).filter((x) =>
      (x.name || "").toLowerCase().includes(t)
    );
  }, [panners, filter]);

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={2}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={3}
            mb={3}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <HStack spacing={3} align="center">
              <Heading size="lg">Panner (Tree-Based)</Heading>
              <Tooltip label="Refresh">
                <IconButton
                  aria-label="Refresh"
                  icon={<HiOutlineRefresh />}
                  size="sm"
                  onClick={refreshHeaders}
                />
              </Tooltip>
              <Button
                size="sm"
                leftIcon={<HiOutlinePlus />}
                onClick={createPanner}>
                New Panner
              </Button>
            </HStack>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={["1fr", "280px 1fr"]}
            gap={3}
            alignItems="start">
            {/* Left: list */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={2}
              backgroundColor="whiteAlpha.800"
              maxH="80vh"
              overflow="hidden">
              <InputGroup size="sm" mb={2}>
                <InputLeftElement pointerEvents="none">
                  <HiOutlineSearch />
                </InputLeftElement>
                <Input
                  placeholder="Search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </InputGroup>

              <Box overflowY="auto" maxH="calc(80vh - 3rem)">
                {filtered.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={2}>
                    No panners
                  </Text>
                ) : (
                  <Stack spacing={1}>
                    {filtered.map((h) => (
                      <HStack
                        key={h._id}
                        spacing={2}
                        p={2}
                        borderWidth="1px"
                        borderRadius="md"
                        cursor="pointer"
                        bg={selectedId === h._id ? "blue.50" : "white"}
                        _hover={{
                          bg: selectedId === h._id ? "blue.100" : "gray.50",
                        }}
                        onClick={() => loadPanner(h._id)}
                        align="center">
                        <Box flex="1" minW={0}>
                          <Tooltip label={h._id}>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              noOfLines={1}>
                              {h.name || "(unnamed)"}
                            </Text>
                          </Tooltip>
                        </Box>
                        <Tooltip label="Load">
                          <IconButton
                            aria-label="Load"
                            icon={<HiOutlineUpload />}
                            size="xs"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadPanner(h._id);
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Right: main form */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={3}
              backgroundColor="whiteAlpha.800"
              minH="80vh">
              {!panner ? (
                <Box opacity={0.8} fontStyle="italic">
                  Select a panner from the left or create a new one.
                </Box>
              ) : (
                <PannerForm
                  panner={panner}
                  setPanner={setPanner}
                  onSave={savePanner}
                  onGenerate={generate}
                  onMapfaculate={mapfaculate}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PannerTreeBased;